import Image from "@tiptap/extension-image";

/**
 * Image node that persists an `uploadKey` (rendered as `data-upload-key`).
 *
 * A freshly-inserted image is shown via a local `blob:` URL for instant preview,
 * while the key ties it to a File registered in the form's `assets.uploads` map.
 * At save time the deferred S3 upload pipeline (see `prepare-upsert-request.ts`
 * and the backend `EmailTemplateImageService`) swaps the blob src for the hosted
 * URL by matching this key. Once uploaded and reloaded the image keeps its
 * `data-upload-key` but its src is already the hosted URL and no matching File
 * exists, so it is left untouched.
 *
 * The `width` attribute holds a CSS length with unit (e.g. `"300px"` or
 * `"50%"`), edited through inputs in the toolbar and rendered as an inline
 * `style` so both px and % are honoured (in the editor and email clients).
 */
const ImageWithUploadKey = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      uploadKey: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-upload-key"),
        renderHTML: (attributes) => {
          if (!attributes.uploadKey) {
            return {};
          }
          return { "data-upload-key": attributes.uploadKey };
        },
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const styleWidth = element.style.width;
          if (styleWidth) {
            return styleWidth;
          }
          // Fallback for the legacy `width="123"` attribute.
          const attrWidth = element.getAttribute("width");
          return attrWidth ? `${attrWidth}px` : null;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) {
            return {};
          }
          return { style: `width:${attributes.width}` };
        },
      },
    };
  },
});

export default ImageWithUploadKey;
